import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useMyMonitoringDeleteSiteDetails } from '@apps-common/apis/my-monitoring/site'
import { useMyMonitoringCreateSiteDetails } from '@apps-common/apis/my-monitoring/site/create-site-details'
import { useMyMonitoringGetSiteDetails } from '@apps-common/apis/my-monitoring/site/get-site-details'
import { useMyMonitoringUpdateSiteDetails } from '@apps-common/apis/my-monitoring/site/update-site-details'
import { useCreateSiteDetails, useDeleteSiteDetails, useGetSiteDetails, useUpdateSiteDetails } from '@apps-common/apis/portal/site-details'
import { SiteDetailsImage } from '@apps-common/components/common'
import { ButtonRounded } from '@apps-common/components/elements'
import { InputSM } from '@apps-common/components/elements/form'
import { VertSpacerMd } from '@apps-common/components/layout'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer,
  ModalTopConfirmActionButton
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { invalidateSiteDetailsQueries } from '@apps-common/lib/invalidateSiteDetailsQueries'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { ISiteDetailsModel } from '@apps-common/types/portal/site-details'
import classNames from 'classnames'
import { ConfirmModalAction } from '../ConfirmModal'
import { ModalButton } from '../ModalButton'
import { SiteImageSelectModal } from './SiteImageSelectModal'

type FormInputs = {
  siteDetailsId: number
  unitId: string
  siteImage: string
  siteName: string
  streetAddress1: string
  streetAddress2: string
  streetAddress3: string
  city: string
  phone: string
}

interface IProps {
  unitId: string
  hideDelete?: boolean
  disableEditing?: boolean
}

export const SiteDetailsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IProps
}) => {
  const [file, setFile] = useState<File | null>()
  const [site, setSite] = useState<ISiteDetailsModel>()

  const { unitId, hideDelete, disableEditing } = componentProps || {}

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormInputs>({ mode: 'onSubmit' })

  const useGetHook = useProjectHook(useGetSiteDetails, useMyMonitoringGetSiteDetails)
  const useCreateHook = useProjectHook(useCreateSiteDetails, useMyMonitoringCreateSiteDetails)
  const useUpdateHook = useProjectHook(useUpdateSiteDetails, useMyMonitoringUpdateSiteDetails)
  const useDeleteHook = useProjectHook(useDeleteSiteDetails, useMyMonitoringDeleteSiteDetails)

  const { isPending: createIsPending, mutate: createSiteDetails } = useCreateHook(true, (data: ISiteDetailsModel) => {
    invalidateSiteDetailsQueries(unitId)
    onDismiss(JSON.stringify(data), ModalDismiss.Confirm)
  })
  const { isPending: updateIsPending, mutate: updateSiteDetails } = useUpdateHook(true, (data: ISiteDetailsModel) => {
    invalidateSiteDetailsQueries(unitId)
    onDismiss(JSON.stringify(data), ModalDismiss.Confirm)
  })
  const { isPending: deleteIsPending, mutate: deleteSiteDetails } = useDeleteHook(true, (data: boolean) => {
    invalidateSiteDetailsQueries(unitId)
    onDismiss(null, ModalDismiss.Confirm)
  })

  const { isLoading, isError, data } = useGetHook({
    config: {
      retry: false,
      staleTime: 0
    },
    unitId: componentProps?.unitId || ''
  })

  const onSubmit = async (sdData: any) => {
    if (sdData.siteDetailsId > 0) {
      updateSiteDetails({ model: getValues() as any, image: file })
    } else {
      createSiteDetails({ model: getValues() as any, image: file })
    }
  }

  useEffect(() => {
    setValue('unitId', componentProps?.unitId)
  }, [componentProps?.unitId])

  useEffect(() => {
    if (data) {
      setSite(data)
      reset(data)
    }
  }, [data])

  const loading = isLoading || isError
  const updating = createIsPending || updateIsPending || deleteIsPending || disableEditing

  const siteImage = (
    <ModalButton
      component={SiteImageSelectModal}
      disabled={disableEditing}
      componentProps={{
        siteImage: site?.siteImage
      }}
      enterAnimation={modalSlideUpEnterAnimation}
      leaveAnimation={modalSlideUpLeaveAnimation}
      backdropDismiss
      classes='relative !opacity-100'
      confirmCallback={({ imgUrl, file }: { imgUrl: string | null; file: File | null }) => {
        if (imgUrl) {
          setSite({
            ...data,
            siteImage: imgUrl
          })
          setValue('siteImage', imgUrl)
          setFile(null)
        } else if (file) {
          setFile(file)
        }
      }}
    >
      {!disableEditing && (
        <div className='absolute top-0 right-0 bg-surface rounded-full p-[1px]'>
          <commonIcons.editCircle className='text-text' />
        </div>
      )}
      <SiteDetailsImage loading={loading} siteName={site?.siteName} url={site?.siteImage} file={file} />
    </ModalButton>
  )

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle='Update' title='Site details' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
        </div>
      </ModalHeaderContainer>
      <ModalBody classes={classNames('!pt-[100px]', disableEditing && '!pb-[40px]')}>
        <form noValidate id='siteDetailsModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div className='flex items-start justify-between'>
            {siteImage}
            {data && !hideDelete && (
              <ModalTopConfirmActionButton
                loading={loading || updating}
                confirmProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete these site details? This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteSiteDetails({ id: data?.siteDetailsId || 0 })}
                text='Delete'
                icon='delete'
                classes='action-error'
              />
            )}
          </div>
          <VertSpacerMd />
          <InputSM
            type='text'
            placeholder='Name of the site'
            label='Site name'
            id='siteName'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('siteName', {
              required: true
            })}
            errors={errors.siteName}
          />
          <InputSM
            type='text'
            placeholder='Address line 1'
            label='Address'
            id='streetAddress1'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('streetAddress1')}
            errors={errors.streetAddress1}
          />
          <InputSM
            type='text'
            placeholder='Address line 2'
            label='Address line 2'
            id='streetAddress2'
            loading={loading}
            disabled={updating}
            registration={register('streetAddress2')}
            errors={errors.streetAddress2}
          />
          <InputSM
            type='text'
            placeholder='Address line 3'
            label='Address line 3'
            id='streetAddress3'
            loading={loading}
            disabled={updating}
            registration={register('streetAddress3')}
            errors={errors.streetAddress3}
          />
          <InputSM
            type='text'
            placeholder='City'
            label='City'
            id='city'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('city')}
            errors={errors.city}
          />
          <InputSM
            type='text'
            placeholder='Phone'
            label='Phone'
            id='phone'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('phone')}
            errors={errors.phone}
          />
        </form>
      </ModalBody>
      {!disableEditing && (
        <ModalFooter>
          <div className='w-full flex items-center justify-start gap-2'>
            <ButtonRounded form='siteDetailsModalForm' type='submit' loading={updating} text='Save' className='action-success' />
            <ButtonRounded
              type='button'
              loading={updating}
              text='Cancel'
              className='action-neutral'
              callback={() => onDismiss(null, 'cancel')}
            />
          </div>
        </ModalFooter>
      )}
    </ModalContainer>
  )
}
